import React, { CSSProperties } from "react";
import { CaretDown, CaretRight } from "react-bootstrap-icons";
import "./Tree.scss";

export interface TreeNode<TData> {
  text?: string;
  data?: TData;
  expanded?: boolean;
  children?: TreeNode<TData>[];
}

interface TreeProps<TData> {
  root: TreeNode<TData>;
  onToggleExpanded: (node: TreeNode<TData>) => void;
  onFocus: (node: TreeNode<TData>) => void;
  onContextMenu: (
    node: TreeNode<TData>,
    positionX: number,
    positionY: number,
  ) => boolean;
  onKeyDown: (code: string, ctrlKey: boolean, altKey: boolean) => boolean;
  style?: CSSProperties | undefined;
  isFocused?: (node: TreeNode<TData>) => boolean;
  onRenderExtra?: (node: TreeNode<TData>) => JSX.Element;
  onRenderIcon?: (node: TreeNode<TData>) => JSX.Element;
}

export function Tree<TData>({
  root,
  onToggleExpanded,
  onFocus,
  onContextMenu,
  onKeyDown,
  style,
  isFocused,
  onRenderIcon,
  onRenderExtra,
}: TreeProps<TData>) {
  const renderTreeNodes = (nodes: TreeNode<TData>[]) => {
    return nodes.map((n) => (
      <li
        tabIndex={0}
        role="button"
        key={n.text}
        className={[
          n.expanded ? "expanded" : undefined,
          isFocused && isFocused(n) ? "focused" : undefined,
        ]
          .filter((s) => s)
          .join(" ")}
        onClick={(e) => {
          onFocus(n);
          e.stopPropagation();
        }}
        onContextMenu={(e) => {
          onContextMenu(n, e.clientX, e.clientY);
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <span>
          {n.children &&
            n.children.length > 0 &&
            (n.expanded ? (
              <CaretDown
                onClick={(e) => {
                  onToggleExpanded(n);
                  e.stopPropagation();
                }}
              />
            ) : (
              <CaretRight
                onClick={(e) => {
                  onToggleExpanded(n);
                  e.stopPropagation();
                }}
              />
            ))}
          {(n.children?.length ?? 0) === 0 && (
            <svg width="1em" height="1em"></svg> // add an invible space the same style as the icons so that it takes the same space in the layout
          )}
          {onRenderIcon && onRenderIcon(n)}
          {n.text}
          {onRenderExtra && onRenderExtra(n)}
        </span>
        {n.children && n.children.length > 0 && (
          <ul
            className="nice-tree"
            {...(!n.expanded && { style: { height: 0 } })}
          >
            {renderTreeNodes(n.children)}
          </ul>
        )}
      </li>
    ));
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLUListElement>,
  ): void => {
    if (onKeyDown(event.code, event.ctrlKey, event.altKey)) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  return root.children && root.children.length > 0 ? (
    <ul
      className="nice-tree"
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKeyDown}
      style={style}
    >
      {renderTreeNodes(root.children)}
    </ul>
  ) : (
    <></>
  );
}

// makes a deep copy of the tree starting from startNode
// the predicate can be used to modify the tree inplace
export function copyTreeNode<TData>(
  startNode: TreeNode<TData>,
  predicate: ((n: TreeNode<TData>) => TreeNode<TData>) | undefined,
) {
  let node = predicate ? predicate(startNode) : startNode;

  if (node.children && node.children.length > 0) {
    node.children = node.children.map((c) => copyTreeNode(c, predicate));
  }

  return { ...node };
}

// finds a specific node using the predicate provided
export function findTreeNode<TData>(
  startNode: TreeNode<TData>,
  predicate: (n: TreeNode<TData>) => boolean,
): TreeNode<TData> | undefined {
  if (predicate(startNode)) return startNode;

  if (startNode.children) {
    for (let ii = 0; ii < startNode.children.length; ++ii) {
      const node = findTreeNode(startNode.children[ii], predicate);

      if (node) {
        return node;
      }
    }
  }

  return undefined;
}

export function findTreeNodeParent<TData>(
  node: TreeNode<TData>,
  startNode: TreeNode<TData>,
  comparer: (a: TData | undefined, b: TData | undefined) => boolean,
) {
  return findTreeNode(
    startNode,
    (n) =>
      (n.children?.findIndex(
        (c) => c.text === node.text && comparer(c.data, node.data),
      ) ?? -1) >= 0,
  );
}

export function getFlattenedTree<TData>(
  startNode: TreeNode<TData>,
  skipInvisible: boolean,
) {
  let list: TreeNode<TData>[] = [];

  if (startNode.children && startNode.children.length > 0) {
    for (let ii = 0; ii < startNode.children.length; ++ii) {
      list.push(startNode.children[ii]);
      if (skipInvisible && startNode.children[ii].expanded !== true) continue;

      list = [
        ...list,
        ...getFlattenedTree(startNode.children[ii], skipInvisible),
      ];
    }
  }
  return list;
}
